import {
  Button,
  Inline,
  Modal,
  type ModalProps,
  notifySuccess,
  Stack,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useCompensationConfiguration,
  useCompensationVariantPayments,
  useCreateCompensationVariantPaymentMutation,
} from '@omnipresentgroup/domains';
import { Form, FormProvider } from '@omnipresentgroup/flexible-forms';
import {
  InputFieldSkeleton,
  RadioFieldSkeleton,
} from '@omnipresentgroup/shared';
import {
  CompensationConfigTypes,
  CompensationSource,
} from '@omnipresentgroup/types';
import type { IChangeEvent } from '@rjsf/core';
import type { RJSFSchema } from '@rjsf/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useContractualVariantPaymentSchema } from '../hooks';
import {
  type ContractualVariantPaymentFormFields,
  type EmployeeData,
  FormAction,
} from '../types';
import { BonusDisclaimerCheckbox } from './BonusDisclaimerCheckbox';

type Props = {
  trigger: ModalProps['trigger'];
  employeeData: EmployeeData;
};

const FORM_ID = 'add-contractual-variant-payment-form';
const FIELD_ID_PREFIX = 'root_';

const FormSkeleton = () => {
  return (
    <Stack gap="16">
      <RadioFieldSkeleton numberOfOptions={2} />
      {Array.from({ length: 3 }, (_, index) => (
        <InputFieldSkeleton key={`input-skeleton-${index}`} />
      ))}
    </Stack>
  );
};

export const AddContractualVariantPaymentModal = ({
  trigger,
  employeeData,
}: Props) => {
  const { modalProps, isOpen } = useModal({ trigger });

  const [modalOpen, setModalOpen] = useState(false);

  const [formValues, setFormValues] = useState<
    ContractualVariantPaymentFormFields | undefined
  >({});

  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const { data: variantPayments, isLoading: isLoadingVariantPayments } =
    useCompensationVariantPayments({
      employeeId: employeeData.id,
      source: CompensationSource.CONTRACTUAL,
    });

  const { data: compensationConfig, isLoading: isLoadingCompensationConfig } =
    useCompensationConfiguration({
      countryCode: employeeData.countryCode,
      source: CompensationSource.CONTRACTUAL,
      enabled: !!employeeData.countryCode,
    });

  const availableItemsConfig = useMemo(
    () =>
      compensationConfig?.variantPaymentsTypes.filter((config) =>
        variantPayments?.every((item) => item.subtype !== config.subtype),
      ) || [],
    [compensationConfig?.variantPaymentsTypes, variantPayments],
  );

  const itemConfig = useMemo(
    () =>
      availableItemsConfig.find(
        (item) => item.subtype === formValues?.subtype,
      ) || undefined,
    [formValues?.subtype, availableItemsConfig],
  );

  const { schema: flexibleSchema, uiSchema: flexibleUiSchema } =
    useContractualVariantPaymentSchema({
      itemConfig,
      itemsConfig: availableItemsConfig,
      currency: employeeData.currency,
      action: FormAction.ADD,
      formValues,
    });

  const {
    mutate: createVariantPayment,
    isPending: isCreatingVariantPayment,
    isSuccess,
  } = useCreateCompensationVariantPaymentMutation({
    employeeId: employeeData.id,
  });

  // Reset form values when modal is closed
  useEffect(() => {
    !modalOpen && setFormValues({});
  }, [modalOpen]);

  // Reset checkbox when type changes
  useEffect(() => {
    setDisclaimerAccepted(false);
  }, [formValues?.type]);

  // Handle form change
  const handleChange = useCallback(
    (
      {
        formData,
      }: IChangeEvent<ContractualVariantPaymentFormFields, RJSFSchema>,
      id?: string,
    ) => {
      if (formData) {
        // Reset dependent fields based on the changed field
        switch (id) {
          case `${FIELD_ID_PREFIX}type`: {
            setFormValues({
              ...formData,
              subtype: undefined,
              recurrence: undefined,
            });
            break;
          }
          case `${FIELD_ID_PREFIX}subtype`: {
            setFormValues({
              ...formData,
              recurrence: undefined,
            });
            break;
          }
          default: {
            setFormValues(formData);
            break;
          }
        }
      }
    },
    [],
  );

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      notifySuccess({
        title: 'Variant payment with has been created successfully',
      });
      setModalOpen(false);
    }
  }, [isSuccess]);

  const isLoading = useMemo(
    () => isLoadingCompensationConfig || isLoadingVariantPayments,
    [isLoadingCompensationConfig, isLoadingVariantPayments],
  );

  return (
    <Modal
      {...modalProps}
      span={4}
      open={isOpen || modalOpen}
      onOpenChange={setModalOpen}
    >
      <Modal.Header title={'Add compensation item'} />
      <Modal.Body>
        {isLoading ? (
          <FormSkeleton />
        ) : (
          <Stack overflow={'hidden'}>
            <FormProvider>
              <Form<ContractualVariantPaymentFormFields>
                id={FORM_ID}
                schema={flexibleSchema}
                uiSchema={flexibleUiSchema}
                formData={formValues}
                onChange={handleChange}
                onSubmit={() => {
                  formValues &&
                    createVariantPayment({
                      type: formValues.type!,
                      subtype: formValues.subtype!,
                      recurrence: formValues.recurrence!,
                      amount: formValues.amount!,
                      reason: formValues.reason!,
                      additionalNotes: formValues.additionalNotes,
                      employeeId: employeeData.id,
                      source: CompensationSource.CONTRACTUAL,
                    });
                }}
                liveOmit
                omitExtraData
              />
            </FormProvider>
            {formValues?.type === CompensationConfigTypes.BONUS && (
              <BonusDisclaimerCheckbox
                disclaimerAccepted={disclaimerAccepted}
                setDisclaimerAccepted={setDisclaimerAccepted}
              />
            )}
          </Stack>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={() => setModalOpen(false)}
            disabled={isCreatingVariantPayment}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={isCreatingVariantPayment}
            disabled={
              isLoading ||
              (formValues?.type === CompensationConfigTypes.BONUS &&
                !disclaimerAccepted)
            }
          >
            Add
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
